import type { Bill, IncomeEvent, SpendingTransaction } from "../api-modules/types.js";
import { savingsRate } from "./fire.js";

/**
 * Saving-rate trend coach — web mirror of
 * `shared/src/util/saving-rate-trend.ts`. Web does NOT import
 * @hearth/shared (5.1 lock); keep the two files in sync.
 *
 * Phase 13 (2026-05-09). See the shared file for citations + status
 * threshold design.
 */

export interface SavingRateTrendInput {
  readonly income_events: readonly IncomeEvent[];
  readonly bills: readonly Bill[];
  readonly spending_transactions: readonly SpendingTransaction[];
  readonly today: string;
  readonly required_savings_rate_pct?: number;
  readonly series_months?: number;
}

export type SavingRateStatus =
  | "on_track"
  | "slipping"
  | "critical"
  | "unknown";

export interface SavingRateMonthPoint {
  readonly period: string;
  readonly trailing_12_rate_pct: number;
}

export interface SavingRateTrendResult {
  readonly current_trailing_12_pct: number;
  readonly prior_year_trailing_12_pct: number;
  readonly mom_delta_pp: number;
  readonly yoy_delta_pp: number;
  readonly series: readonly SavingRateMonthPoint[];
  readonly required_savings_rate_pct: number | undefined;
  readonly status: SavingRateStatus;
  readonly current_window: {
    readonly net_income_cents: number;
    readonly total_outflows_cents: number;
    readonly savings_cents: number;
    readonly monthly_net_savings_cents: number;
  };
}

const DEFAULT_SERIES_MONTHS = 12;
const SLIPPING_RATIO = 0.75;

export function evaluateSavingRateTrend(
  input: SavingRateTrendInput,
): SavingRateTrendResult {
  const seriesMonths = input.series_months ?? DEFAULT_SERIES_MONTHS;

  const series: SavingRateMonthPoint[] = [];
  for (let i = seriesMonths - 1; i >= 0; i--) {
    const periodToday = subMonthsIso(input.today, i);
    const r = savingsRate({
      income_events: input.income_events,
      bills: input.bills,
      spending_transactions: input.spending_transactions,
      today: periodToday,
      lookback_months: 12,
    });
    series.push({
      period: monthEndPeriod(periodToday),
      trailing_12_rate_pct: r.savings_rate_pct,
    });
  }

  const current = savingsRate({
    income_events: input.income_events,
    bills: input.bills,
    spending_transactions: input.spending_transactions,
    today: input.today,
    lookback_months: 12,
  });

  const priorYear = savingsRate({
    income_events: input.income_events,
    bills: input.bills,
    spending_transactions: input.spending_transactions,
    today: subMonthsIso(input.today, 12),
    lookback_months: 12,
  });

  const priorMonth = savingsRate({
    income_events: input.income_events,
    bills: input.bills,
    spending_transactions: input.spending_transactions,
    today: subMonthsIso(input.today, 1),
    lookback_months: 12,
  });

  let status: SavingRateStatus;
  if (input.required_savings_rate_pct === undefined) {
    status = "unknown";
  } else if (current.savings_rate_pct >= input.required_savings_rate_pct) {
    status = "on_track";
  } else if (
    current.savings_rate_pct >=
    SLIPPING_RATIO * input.required_savings_rate_pct
  ) {
    status = "slipping";
  } else {
    status = "critical";
  }

  return {
    current_trailing_12_pct: current.savings_rate_pct,
    prior_year_trailing_12_pct: priorYear.savings_rate_pct,
    mom_delta_pp: current.savings_rate_pct - priorMonth.savings_rate_pct,
    yoy_delta_pp: current.savings_rate_pct - priorYear.savings_rate_pct,
    series,
    required_savings_rate_pct: input.required_savings_rate_pct,
    status,
    current_window: {
      net_income_cents: current.net_income_cents,
      total_outflows_cents: current.total_outflows_cents,
      savings_cents: current.savings_cents,
      monthly_net_savings_cents: current.monthly_net_savings_cents,
    },
  };
}

function subMonthsIso(today: string, months: number): string {
  const [yStr, mStr, dStr] = today.split("-");
  const y = Number(yStr);
  const m = Number(mStr);
  const d = Number(dStr);
  const total0 = y * 12 + (m - 1) - months;
  const newY = Math.floor(total0 / 12);
  const newM0 = total0 % 12;
  const lastDay = new Date(Date.UTC(newY, newM0 + 1, 0)).getUTCDate();
  const newD = Math.min(d, lastDay);
  return `${pad4(newY)}-${pad2(newM0 + 1)}-${pad2(newD)}`;
}

function monthEndPeriod(today: string): string {
  const [yStr, mStr] = today.split("-");
  const y = Number(yStr);
  const m = Number(mStr);
  const total0 = y * 12 + (m - 1) - 1;
  const newY = Math.floor(total0 / 12);
  const newM0 = total0 % 12;
  return `${pad4(newY)}-${pad2(newM0 + 1)}`;
}

function pad2(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}
function pad4(n: number): string {
  return n.toString().padStart(4, "0");
}
